import type { Api_Product, Api_Product_Unit, Api_Video } from '@core/api'
import type { CartHydrationMode } from '@core/shopping/model/actions'

/**
 * Different from ICartContentItem, product is required in this interface.
 * This interface is used to represent the content of the shopping cart after
 * mapping them with the products. Units without a matching product will be
 * discarded.
 */
export interface ICartContentItemWithProduct {
  product: Api_Product
  unit: Api_Product_Unit
  quantity: number
}

export type ICartContent = ICartContentItem[]

export interface ICartContentItem {
  // When rendering a cart item, player will try to find the product from the
  // products downloaded from the videos or hydrated by the site. If it can't
  // find it, it will use the product from the cart item.
  // This is only needed when it is impossible to hydrate the cart.
  product?: Api_Product
  unit: Api_Product_Unit
  quantity: number
}

/**
 * A callback that triggers when Firework player attempts to update the cart. It
 * is recommended to sync with the external cart backend in the callback and
 * return the cart item quantities verified by the server.
 */
export type CartUpdatedCallback = (payload: {
  product: Api_Product
  productUnit: Api_Product_Unit
  quantity: number
  previousQuantity: number
  video: Api_Video | null
}) => Promise<number>

export interface HydratedCartItem {
  product: Api_Product
  unitId: string
  quantity: number
}
/**
 * A callback that triggers when Firework player displays the cart (or cart
 * icon). It is recommended to sync the external cart in the callback. The
 * callback should return the latest product data of the cart items and the cart
 * item quantities.
 */
export type CartDisplayedCallback = () => Promise<
  HydratedCartItem[] | undefined
>

/**
 * A callback that triggers when Firework player finishes loading products. It
 * is recommended to hydrate products in this callback if the OMS has up-to-date
 * product information or things that are customized to local users.
 *
 * When productNotFound === true, the callback may return only products that were
 * successfully found/hydrated; products not in the returned list will be hidden
 * when channel config product_ext_id_not_found_behavior is 'hide_product'. When 'show_original' or undefined, return
 * all products (including originals for not-found).
 *
 * @returns A promise that resolves to a list of hydrated products if all the
 *     products can be loaded at once. A list of promises that resolve to
 *     individual product if the products need to be loaded one by one.
 */
export type ProductsLoadedCallback = (payload: {
  video: Api_Video | null
  products: Api_Product[]
  product_ext_id_not_found_behavior?: 'show_original' | 'hide_product'
}) => Promise<Api_Product[] | undefined>

/**
 * A callback that triggers each time a widget finishes loading a page of
 * videos (initial fetch and each loadMore). It receives all videos currently
 * loaded for the widget together with a flat list of their products. Use this
 * to batch-hydrate products at the widget level instead of per-video.
 *
 * @returns A promise that resolves to a list of hydrated products.
 */
export type WidgetProductsLoadedCallback = (payload: {
  widgetId: string
  name: string
  videos: Api_Video[]
  products: Api_Product[]
}) => Promise<Api_Product[] | undefined>

/**
 * Called when the user taps "Size Guide" on the instream PDP. The host should
 * open or render its own size guide (modal, new window, etc.).
 */
export type SizeGuideRequestCallback = (payload: {
  product: Api_Product
}) => void

export interface IShoppingState {
  cart: ICartContent
  cartCurrency: string
  cartUrl: string
  cartAddToCartText: string
  cartItemInCartText: string
  productsHydrated: Record<string, Api_Product>
  cartUpdatedCallback?: CartUpdatedCallback
  cartDisplayedCallback?: CartDisplayedCallback
  productsLoadedCallback?: ProductsLoadedCallback
  widgetProductsLoadedCallback?: WidgetProductsLoadedCallback
  sizeGuideRequestCallback?: SizeGuideRequestCallback
  pendingCartUpdate: number // number of pending cart updates.
  lastCartUpdateTs: number // timestamp of the last time the cart was updated.
  quickAdd: boolean
  quickView: boolean
  hydrationMode: CartHydrationMode
  cartId?: string
}

/**
 *
 */
export function createShoppingState(): IShoppingState {
  return {
    cart: [],
    cartCurrency: '',
    cartUrl: '',
    cartAddToCartText: '',
    cartItemInCartText: '',
    productsHydrated: {},
    pendingCartUpdate: 0,
    lastCartUpdateTs: 0,
    quickAdd: false,
    quickView: false,
    hydrationMode: 'merge',
  }
}
